import React from 'react';
import { ModalProps } from '@modals/types';
import Overlay from '@components/Overlay';
import { Button, Card, Divider, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import DevText from '@components/Text';
import { getStringRequiredValidator } from '@app/hooks/validation/functions';
import { useCustomSelectFormField, useForm } from '@app/hooks';
import { ToastUtils } from '@app/data/utils';
import SelectFormField from '@app/components/SelectFormField';
import { userEmailNotificationLevelInformation } from '../../consts';
import { UserEmailMinNotificationLevel } from 'dy-frontend-http-repository/lib/data/enums';
import { updateUserEmailSettings } from './store/effects';
import { userEmailSettingsApi } from '../../store/apis';

export interface UpdateEmailNotificationLevelModalProps {
    currentNotificationLevel: UserEmailMinNotificationLevel;
    userId: ID;
}

type Props = ModalProps<UpdateEmailNotificationLevelModalProps>;

const notificationLevelValidators = [getStringRequiredValidator()];

const UpdateEmailNotificationLevelModal: React.FC<Props> = ({ closeModal, data }) => {
    // Legacy "high" is shown and preselected as "All" (low)
    const currentNotificationLevel =
        data?.currentNotificationLevel === UserEmailMinNotificationLevel.HIGH
            ? UserEmailMinNotificationLevel.LOW
            : data?.currentNotificationLevel ?? null;

    const notificationLevel = useCustomSelectFormField<string | null>({
        id: 'email-notification-level',
        initialValue: currentNotificationLevel,
        formatValue: (date) => (date ? date.toString() : ''),
        validators: notificationLevelValidators,
    });

    const form = useForm({
        fields: [notificationLevel],
        apiCall: async () => {
            if (!data) {
                return { response: false };
            }

            try {
                // Update notification level
                await updateUserEmailSettings({
                    userId: data.userId,
                    input: { min_notification_level: notificationLevel.value },
                });

                // Update local state notification level value
                userEmailSettingsApi.setNotificationLevel({
                    newNotificationLevel: notificationLevel.value as UserEmailMinNotificationLevel,
                });

                // Close modal
                closeModal?.();

                return { response: true };
            } catch (e) {
                // TODO: handle error
                throw e;
            }
        },
        onFailure: (e) => {
            // TODO: handle error
            console.error(e);
            ToastUtils.showToast({
                message: e,
                intent: Intent.DANGER,
            });
        },
    });

    const handleModalClose = () => {
        if (form.isSubmitting) {
            return;
        }

        closeModal?.();
    };

    if (!data) {
        handleModalClose();
        return null;
    }

    // Selectable email notification levels
    const selectableNotificationLevels = [
        UserEmailMinNotificationLevel.LOW,
        UserEmailMinNotificationLevel.NORMAL,
        UserEmailMinNotificationLevel.ONLY_INVOICES,
        UserEmailMinNotificationLevel.OFF,
    ];

    // Same notification level selected flag
    const isSameNotificationLevelSelected = currentNotificationLevel === notificationLevel.value;

    const renderForm = () => {
        return (
            <form onSubmit={form.handleFormSubmit}>
                <SelectFormField
                    field={notificationLevel}
                    items={selectableNotificationLevels}
                    formGroupProps={{ label: 'Notification level' }}
                    selectProps={{ selectButtonProps: { placeholder: 'Select notification level' } }}
                    itemToReadableFormatter={(item) => userEmailNotificationLevelInformation[item].name}
                />

                <Divider className="mb-2" />

                <Flex align="center" justify="space-between">
                    <DevText muted>Proceed with notification level update?</DevText>
                    <div>
                        <Button minimal onClick={closeModal} className="mr-1" disabled={form.isSubmitting}>
                            No, cancel
                        </Button>
                        <Button
                            intent={Intent.PRIMARY}
                            loading={form.isSubmitting}
                            disabled={form.hasFieldErrors || isSameNotificationLevelSelected}
                            type="submit"
                        >
                            Yes, update level
                        </Button>
                    </div>
                </Flex>
            </form>
        );
    };

    return (
        <Overlay isOpen onClose={handleModalClose}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="start" justify="space-between">
                    <div>
                        <Heading type="h4" className="mb-1">
                            Update notification level
                        </Heading>
                    </div>
                    <Button minimal icon="cross" onClick={closeModal} disabled={form.isSubmitting} />
                </Flex>

                <Divider className="mb-2" />

                {renderForm()}
            </Card>
        </Overlay>
    );
};

export default UpdateEmailNotificationLevelModal;
